import React, { useMemo, useState } from "react"
import { Box, Card, CardContent, useMediaQuery } from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/styles"
import { NavLink } from "react-router-dom"
import { ActionHeader } from "./ActionHeader"
import { ActionContent } from "./ActionContent"
import { ActionResolve } from "./ActionResolve"
import { CommentList } from "./CommentList"
import { AddComment } from "./AddComment"
import { InfoSection, FormatDate, ActionCreator, RowBox } from ".."
import { useJobUtils } from "../../utils"

const useStyles = makeStyles((theme) => ({
  action: {
    marginBottom: theme.spacing(3),
    padding: 0,
  },
  content: {
    paddingTop: 0,
    "&:last-child": {
      paddingBottom: theme.spacing(1),
    },
  },
  section: {
    borderBottom: "1px solid #EFEFEF",
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
    "&:last-child": {
      borderBottom: "none",
    },
  },
  compactSection: {
    borderBottom: "1px solid #EFEFEF",
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
    paddingTop: theme.spacing(0.5),
  },
  sectionHeader: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  sectionHighlight: {
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: "#fafcff",
  },
  link: {
    color: theme.palette.primary.main,
  },
}))

const Action = ({ action, onDelete, onCommentCreated }) => {
  const theme = useTheme()
  const xs = useMediaQuery(theme.breakpoints.down("xs"))
  const classes = useStyles({ xs })
  const { canNavigateToJob, getNavigateToLink } = useJobUtils()
  const [edit, setEdit] = useState(false)
  const { job, jobStep: step } = action

  const handleEdit = () => {
    setEdit(true)
  }
  const handleClose = () => {
    setEdit(false)
  }

  const jobLink = useMemo(
    () => (job && canNavigateToJob(job) ? getNavigateToLink(job) : null),
    [canNavigateToJob, getNavigateToLink, job]
  )

  const hasJob = Boolean(job)
  const hasJobStep = Boolean(step)

  return (
    <>
      {edit && <ActionCreator open={edit} edit={action} job={action.job} step={action.jobStep} onClose={handleClose} />}

      <Card className={classes.action} elevation={3}>
        <CardContent className={classes.content}>
          <div className={`${classes.section} ${classes.sectionHeader}`}>
            <ActionHeader {...{ action, onEdit: handleEdit, onDelete }} />
          </div>
          {hasJob && (
            <div className={classes.compactSection}>
              <InfoSection title="Location" value={job.location.name} px={0} />
            </div>
          )}
          {hasJob && (
            <div className={classes.compactSection}>
              <InfoSection
                title="Job"
                value={
                  <RowBox component={NavLink} to={jobLink} className={classes.link}>
                    <Box>
                      {job.title} - <FormatDate value={job.availableFrom} />
                    </Box>
                  </RowBox>
                }
                px={0}
              />
            </div>
          )}
          {hasJobStep && (
            <div className={classes.compactSection}>
              <InfoSection title="Step" value={step.name} px={0} />
            </div>
          )}
          <div className={classes.section}>
            <ActionContent action={action} />
          </div>
          <div className={`${classes.compactSection} ${classes.sectionHighlight}`}>
            <ActionResolve action={action} />
          </div>
          <CommentList subject={action} />
          <div className={classes.section}>
            <AddComment action={action} onCommentCreated={onCommentCreated} placeholder="Write a note..." />
          </div>
        </CardContent>
      </Card>
    </>
  )
}

export { Action }
